/**
 * Scoped Video section controller.
 * Integrates with theme DeferredMedia without modifying the global class.
 */
class VideoMediaPlayer extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    this.media = this.querySelector('deferred-media');
    this.poster = this.querySelector('[id^="Deferred-Poster-"]');
    this.closeButton = this.querySelector('.video-section__close');
    this.expandEnabled = this.dataset.expand !== 'false';
    this.loopEnabled = this.dataset.loop === 'true';

    this.onPosterClick = this.handlePosterClick.bind(this);
    this.onCloseClick = this.handleClose.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onEnded = this.handleEnded.bind(this);
    this.onSectionUnload = this.handleSectionUnload.bind(this);

    if (this.poster) {
      this.poster.addEventListener('click', this.onPosterClick);
    }
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.onCloseClick);
    }

    this.attributeObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'loaded' && this.media.hasAttribute('loaded')) {
          this.enterPlayingState();
        }
      });
    });

    if (this.media) {
      this.attributeObserver.observe(this.media, {
        attributes: true,
        attributeFilter: ['loaded'],
      });
    }

    document.addEventListener('keyup', this.onKeydown);
    document.addEventListener('shopify:section:unload', this.onSectionUnload);
  }

  disconnectedCallback() {
    this.teardown();
  }

  handleSectionUnload(event) {
    const section = event.target;
    if (section && section.contains(this)) {
      this.teardown();
    }
  }

  teardown() {
    if (!this._bound) return;
    this._bound = false;

    if (this.poster) this.poster.removeEventListener('click', this.onPosterClick);
    if (this.closeButton) this.closeButton.removeEventListener('click', this.onCloseClick);
    document.removeEventListener('keyup', this.onKeydown);
    document.removeEventListener('shopify:section:unload', this.onSectionUnload);
    if (this.attributeObserver) this.attributeObserver.disconnect();
    this.unbindHostedEnded();
  }

  handlePosterClick() {
    // DeferredMedia loads content on the same click; apply expand immediately.
    if (this.expandEnabled) {
      this.applyExpanded(true);
    }
  }

  enterPlayingState() {
    if (!this.media) return;

    this.classList.add('is-playing');
    this.media.classList.add('is-playing');
    this.classList.remove('is-closing');
    this.media.classList.remove('is-closing');

    if (this.expandEnabled) {
      this.applyExpanded(true);
    }

    if (this.poster) {
      this.poster.setAttribute('aria-expanded', 'true');
    }

    if (this.closeButton) {
      this.closeButton.hidden = false;
    }

    this.bindHostedEnded();
  }

  applyExpanded(expanded) {
    if (!this.media) return;
    this.classList.toggle('is-expanded', expanded);
    this.media.classList.toggle('is-expanded', expanded);
  }

  bindHostedEnded() {
    this.unbindHostedEnded();
    if (this.loopEnabled) return;

    const video = this.media.querySelector(':scope > video');
    if (!video) return;

    this._hostedVideo = video;
    video.addEventListener('ended', this.onEnded);
  }

  unbindHostedEnded() {
    if (this._hostedVideo) {
      this._hostedVideo.removeEventListener('ended', this.onEnded);
      this._hostedVideo = null;
    }
  }

  handleEnded() {
    this.handleClose();
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') return;
    if (!this.media || !this.media.classList.contains('is-playing')) return;
    this.handleClose();
  }

  handleClose(event) {
    if (event) event.preventDefault();
    if (!this.media || !this.media.hasAttribute('loaded')) return;

    this.classList.add('is-closing');
    this.media.classList.add('is-closing');

    this.stopPlayback();
    this.removeLoadedContent();

    this.media.removeAttribute('loaded');
    this.media.removeAttribute('loading');

    this.classList.remove('is-playing', 'is-expanded');
    this.media.classList.remove('is-playing', 'is-expanded');

    if (this.poster) {
      this.poster.setAttribute('aria-expanded', 'false');
    }

    if (this.closeButton) {
      this.closeButton.hidden = true;
    }

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const finish = () => {
      this.classList.remove('is-closing');
      this.media.classList.remove('is-closing');
      if (this.poster) this.poster.focus();
    };

    if (prefersReduced) {
      finish();
    } else {
      window.setTimeout(finish, 320);
    }
  }

  stopPlayback() {
    const video = this.media.querySelector(':scope > video');
    if (video) {
      try {
        video.pause();
        video.currentTime = 0;
      } catch (e) {
        // ignore seek errors on some browsers
      }
    }

    const videoSection = this.media.querySelector('video-section');
    if (!videoSection) return;

    const player = videoSection.player;
    if (!player) return;

    try {
      if (videoSection.dataset.type === 'youtube') {
        if (typeof player.stopVideo === 'function') player.stopVideo();
        else if (typeof player.pauseVideo === 'function') player.pauseVideo();
        if (typeof player.seekTo === 'function') player.seekTo(0, true);
      } else if (videoSection.dataset.type === 'vimeo') {
        if (typeof player.pause === 'function') player.pause();
        if (typeof player.setCurrentTime === 'function') player.setCurrentTime(0);
      }
    } catch (e) {
      // player may already be tearing down
    }
  }

  removeLoadedContent() {
    if (!this.media) return;

    Array.from(this.media.children).forEach((child) => {
      if (child === this.poster) return;
      if (child === this.closeButton) return;
      if (child.tagName === 'TEMPLATE') return;
      child.remove();
    });

    this.unbindHostedEnded();
  }
}

if (!customElements.get('video-media-player')) {
  customElements.define('video-media-player', VideoMediaPlayer);
}
